import React, { forwardRef, useEffect, useRef, useState, } from 'react';
import classNames from 'classnames';
import { isWeb } from '@uni/env';
import { CnButton } from '@/components/cn-button';
import { CnIcon } from '@/components/cn-icon';
import { CnReadonly } from '@/components/cn-readonly';
import { isUndef, isNil, isNumber } from '@/utils/func';
import { mergeProps } from '@/utils/with-default-props';
import isFunction from 'lodash/isFunction';
import get from 'lodash/get';
import { useDeprecated, useGuid } from './hooks';
import { clamp, getDefaultValue, toNumber } from './utils';
export function getValueFromEvents(e) {
    if (!isUndef(e.value)) {
        return e.value;
    }
    if (get(e, 'target.value')) {
        return e.target.value;
    }
    if (get(e, 'detail.value')) {
        return e.target.value;
    }
    if (get(e, 'originalEvent.detail.value')) {
        return e.originalEvent.detail.value;
    }
}
/**
 * 用于表单中判断无效值
 * eg:
 *   isInvalidValue('') : true
 *   isInvalidValue(undefined) : true
 *   isInvalidValue(null) : true
 */
export function isInvalidValue(val) {
    return isNil(val) || val === '';
}
const MAX_NUMBER_PICKER_THRESHOLD = 9999999;
const isIOSH5 = isWeb && window.navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
function getInitInputValue(props) {
    let initialValue;
    if ('value' in props) {
        initialValue = props.value;
    }
    else if (!isUndef(props.defaultValue)) {
        initialValue = props.defaultValue;
    }
    return !isInvalidValue(initialValue) ? toNumber(initialValue) : '';
}
function isDecreaseDisabled(value, min) {
    if (!isNumber(value)) {
        return false;
    }
    return min !== -Infinity && value <= min;
}
function isIncreaseDisabled(value, max) {
    if (!isNumber(value)) {
        return false;
    }
    return max !== Infinity && value >= max;
}
function getPrecision(precision, step) {
    if (typeof precision !== 'number' || precision < 0) {
        precision = 0;
    }
    const stepString = step.toString();
    let pcs = 0;
    if (stepString.indexOf('.') >= 0) {
        pcs = stepString.length - stepString.indexOf('.') - 1;
    }
    return Math.max(pcs, precision);
}
function getPrecisionFactor(precision) {
    return Math.pow(10, precision);
}
function hackChrome(value, precision) {
    if (precision >= 0) {
        return Number(Number(value).toFixed(precision));
    }
    return value;
}
function getFormattedValue(inputValue, format, innerAfter, label) {
    const formatedValue = format ? format(`${inputValue}`) : `${inputValue}`;
    const formatedValueWithInnerAfter = `${formatedValue}${innerAfter}`;
    const vhasLabel = label + formatedValueWithInnerAfter;
    return vhasLabel;
}
const defaultProps = {
    size: 'medium',
    readOnly: false,
    disabled: false,
    hideButton: true,
    precisionProp: 0,
    label: '',
    innerAfter: '',
    controlled: false,
};
const NumberPicker = (props, ref) => {
    const { className, prefix = 'cn-ui-m-', value, defaultValue, max = MAX_NUMBER_PICKER_THRESHOLD, min = -MAX_NUMBER_PICKER_THRESHOLD, step = 1, keybordType: _k1, keyboardType: _k2, precision: precisionProp, hideButton, readOnly, size, disabled, label, innerAfter, 
    // focused: focusedProp = false,
    inputStyle = props.style, onBeforeChange = () => Promise.resolve(), 
    // @ts-ignore
    onDisableClick: onDisableClickProp, 
    // onDisabledClick: onDisabledClickProp,
    format, onChange = () => { }, style = {}, onInput, onFocus = () => { }, onBlur = () => { }, onCorrect = () => { }, ...others } = mergeProps(defaultProps, props);
    const clsPrefix = `${prefix}numberpicker`;
    const isControlled = 'value' in props;
    // @ts-ignore
    // const onDisabledClick = onDisabledClickProp || onDisableClickProp;
    const precision = getPrecision(precisionProp, step);
    const factor = getPrecisionFactor(precision);
    const inputRef = useRef(null);
    // 如果值无效时，给出的一个默认值
    const defaultValAtEmpty = getDefaultValue(min, max, MAX_NUMBER_PICKER_THRESHOLD);
    // 输入框焦点
    const [focused, setFocused] = useState(!!props.focused);
    // 输入框值
    const [inputValue, setInputValue] = useState(getInitInputValue(props));
    const inputId = useGuid('mt-np-input-');
    // 按钮状态 disabled
    const [decreaseDisabled, setDecreaseDisabled] = useState(isDecreaseDisabled(inputValue, min));
    const [increaseDisabled, setIncreaseDisabled] = useState(isIncreaseDisabled(inputValue, max));
    const keyboardType = useDeprecated('NumberPicker', props, 'keybordType', 'keyboardType', 'number');
    useEffect(() => {
        const fn = () => {
            if (isControlled) {
                if (isInvalidValue(value)) {
                    setInputValue('');
                    updateInputVal('');
                }
                else {
                    setInputValue(toNumber(props.value));
                    updateInputVal(getFormattedValue(props.value, format, innerAfter, label));
                }
            }
        };
        fn();
    });
    // 当输入框的值改变时，修改按钮disabled状态
    useEffect(() => {
        setDecreaseDisabled(isDecreaseDisabled(inputValue, min));
        setIncreaseDisabled(isIncreaseDisabled(inputValue, max));
    }, [inputValue, max, min]);
    async function handleChange(numValue, e) {
        // 只读
        if (readOnly) {
            return;
        }
        // 修改前的回调
        // try {
        await onBeforeChange(numValue);
        // } catch (error) {
        //   throw error;
        // }
        doChange(numValue, e);
    }
    function doChange(val, e) {
        if (!isControlled) {
            // 修改状态
            setInputValue(val);
            updateInputVal(getFormattedValue(val, format, innerAfter, label));
        }
        // onChange 回调
        onChange(val, e);
    }
    function handleFocus(e) {
        updateInputVal(inputValue);
        setFocused(true);
        if (isFunction(onFocus)) {
            onFocus(e);
        }
    }
    function updateInputVal(newVal) {
        const cur = inputRef.current;
        if (isWeb && cur) {
            cur.value = '';
            cur.value = newVal;
        }
    }
    function handleBlur(e) {
        const val = toNumber(getValueFromEvents(e));
        if (isFunction(onBlur)) {
            onBlur(e);
        }
        // 如果重置了最小值，则保留用户的最小值
        if (isInvalidValue(val)) {
            setFocused(false);
            handleChange('', {
                ...e,
                originalValue: getValueFromEvents(e),
            });
            return;
        }
        const limitedValue = clamp(val, min, max);
        // notice: auto correct special input condition:  `1.`
        if (!isControlled) {
            updateInputVal(getFormattedValue(limitedValue, format, innerAfter, label));
        }
        let result = (factor * limitedValue) / factor;
        result = hackChrome(result, precision);
        if (val !== result) {
            if (!isControlled) {
                updateInputVal(getFormattedValue(result, format, innerAfter, label));
            }
            onCorrect(result, val);
        }
        setFocused(false);
        handleChange(result, {
            ...e,
            originalValue: getValueFromEvents(e),
        });
    }
    function handleInput(e) {
        if (isFunction(onInput)) {
            const val = toNumber(getValueFromEvents(e));
            let correctVal = val;
            if (isInvalidValue(val)) {
                correctVal = defaultValAtEmpty;
                return;
            }
            const newValue = clamp(val, min, max);
            const result = (factor * newValue) / factor;
            correctVal = hackChrome(result, precision);
            onInput(val, correctVal, e);
        }
    }
    function handleDecrease(e) {
        if (isInvalidValue(inputValue)) {
            handleChange(defaultValAtEmpty, e);
            return;
        }
        const newValue = clamp(inputValue - step, min, max);
        let result = (factor * newValue) / factor;
        result = hackChrome(result, precision);
        if (newValue !== result && isFunction(onCorrect)) {
            onCorrect(result, newValue);
        }
        handleChange(result, e);
    }
    function handleIncrease(e) {
        if (isInvalidValue(inputValue)) {
            handleChange(defaultValAtEmpty, e);
            return;
        }
        const newValue = clamp(inputValue + step, min, max);
        let result = (factor * newValue) / factor;
        result = hackChrome(result, precision);
        if (newValue !== result && isFunction(onCorrect)) {
            onCorrect(result, newValue);
        }
        handleChange(result, e);
    }
    // const handleDisableClick = (actionType: string, e: object) => {
    //   if (isFunction(onDisabledClick)) {
    //     onDisabledClick(actionType, e);
    //   }
    // };
    const classNameObj = {
        numberPicker: classNames(`${clsPrefix}`, `${clsPrefix}--${size}`),
        textInput: classNames(`${clsPrefix}-input`, `${clsPrefix}-input--${size}`, {
            [`${clsPrefix}-input--focused`]: focused,
            // note: there's rendering exception in safari, use `isIOSH5` to circumvent
            [`${clsPrefix}-input--disabled`]: !isIOSH5 && disabled,
            [`${clsPrefix}-input--readonly`]: readOnly,
            [`${clsPrefix}-input--no-button`]: hideButton,
        }),
        decrease: classNames(`${clsPrefix}-button`, `${clsPrefix}-button-decrease`, `${clsPrefix}-button--${size}`, {
            [`${clsPrefix}-button--disabled`]: disabled || decreaseDisabled,
        }),
        decreaseIcon: classNames(`${clsPrefix}-button-icon`, `${clsPrefix}-button-icon--${size}`, {
            [`${clsPrefix}-button-icon--disabled`]: disabled || decreaseDisabled,
        }),
        increase: classNames(`${clsPrefix}-button`, `${clsPrefix}-button-increase`, `${clsPrefix}-button--${size}`, {
            [`${clsPrefix}-button--disabled`]: disabled || increaseDisabled,
        }),
        increaseIcon: classNames(`${clsPrefix}-button-icon`, `${clsPrefix}-button-icon--${size}`, {
            [`${clsPrefix}-button-icon--disabled`]: disabled || increaseDisabled,
        }),
    };
    if (readOnly) {
        return (React.createElement(CnReadonly, null, getFormattedValue(inputValue, format, innerAfter, label)));
    }
    return (React.createElement("div", { style: style, className: classNames(classNameObj.numberPicker, className), ref: ref, ...others },
        !hideButton ? (React.createElement(CnButton, { disabled: disabled || decreaseDisabled, type: "normal", size: size, className: classNameObj.decrease, onClick: handleDecrease },
            React.createElement(CnIcon, { type: "minus", className: classNameObj.decreaseIcon }))) : null,
        React.createElement("input", { id: inputId, className: [
                classNameObj.textInput,
                style?.width ? `${clsPrefix}-input--no-width` : '',
            ].join(' '), ref: inputRef, defaultValue: getFormattedValue(inputValue, format, innerAfter, label), disabled: !!(disabled || readOnly), type: (label || format || innerAfter) && !focused
                ? 'text'
                : keyboardType, onFocus: handleFocus, onBlur: handleBlur, onInput: handleInput, style: inputStyle }),
        !hideButton ? (React.createElement(CnButton, { disabled: disabled || increaseDisabled, type: "normal", size: size, className: classNameObj.increase, onClick: handleIncrease },
            React.createElement(CnIcon, { type: "add", className: classNameObj.increaseIcon }))) : null));
};
NumberPicker.displayName = 'CnNumberPicker';
const CnNumberPicker = forwardRef(NumberPicker);
export { CnNumberPicker };
